import { useState } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useToast } from '@/hooks/useToast';
import type { Equipamento } from '../models';
import { ehItemSimples, normalizarPatrimonio } from '../models';
import { equipamentoService } from '../services/equipamentoService';
import { useNomesColaboradores } from '../hooks/data/useNomesColaboradores';
import { TIPO_ATIVO_LABELS } from '../pages/EquipamentosListPage/components/Ativos/ativos.definitions';
import { descreverSituacao } from '../pages/EquipamentosListPage/components/Ativos/ativoHistorico.format';
import { situacaoDoAtivo } from '../pages/EquipamentosListPage/components/Ativos/ativos.utils';
import type { FormFichaAtivo } from '../pages/EquipamentosListPage/components/Ativos/ativoFicha.form';
import CadastrarAtivoDrawer from './CadastrarAtivoDrawer';

export interface ValorBuscaAtivo {
  patrimonio: string;
  nSerie: string;
  /** Ativo do Inventário escolhido (só com ele dá pra vincular/enviar). */
  encontrado: Equipamento | null;
}

interface BuscaAtivoInventarioProps {
  valor: ValorBuscaAtivo;
  /** Sempre o valor inteiro (uma chamada só) — quem usa pode guardar tudo de uma vez. */
  onChange: (valor: ValorBuscaAtivo) => void;
  /** Envio (sai da Raro): avisa quando o ativo não parece disponível (em uso, manutenção...). */
  paraEnvio?: boolean;
  /** Pra pré-preencher o "Cadastrar agora" (ex.: tipo/modelo do item da Movimentação). */
  cadastroInicial?: Partial<FormFichaAtivo>;
  /** Campos um embaixo do outro, com rótulo (painel estreito, ex. gaveta da Movimentação). */
  empilhado?: boolean;
}

type Estado = 'inicial' | 'nao_encontrado' | 'recusado' | 'varios';

/** Situações em que o ativo está livre pra sair num envio. */
const SITUACOES_DISPONIVEIS = ['em_estoque', 'em_configuracao', 'em_preparo'];

/**
 * Busca no Inventário (substitui a antiga busca no GLPI — o inventário todo já está no
 * Digital): acha o ativo por patrimônio ou nº de série e mostra a Situação. Não achou →
 * "cadastrar agora?" abre o Novo ativo já preenchido; recusando, não dá pra vincular.
 */
export default function BuscaAtivoInventario({
  valor,
  onChange,
  paraEnvio = false,
  cadastroInicial,
  empilhado = false,
}: BuscaAtivoInventarioProps) {
  const toast = useToast();
  const nomeColaborador = useNomesColaboradores();
  const [estado, setEstado] = useState<Estado>('inicial');
  const [buscando, setBuscando] = useState(false);
  const [cadastroAberto, setCadastroAberto] = useState(false);
  const { patrimonio, nSerie, encontrado } = valor;

  const escolher = (ativo: Equipamento) => {
    onChange({ patrimonio: normalizarPatrimonio(ativo.patrimonio) ?? patrimonio, nSerie: ativo.nSerie ?? nSerie, encontrado: ativo });
    setEstado('inicial');
  };

  const buscar = async () => {
    if (!patrimonio.trim() && !nSerie.trim()) return;
    setBuscando(true);
    try {
      const resultados = await equipamentoService.buscarDuplicados(patrimonio, nSerie);
      if (resultados.length === 1) {
        escolher(resultados[0]);
      } else {
        onChange({ ...valor, encontrado: null });
        setEstado(resultados.length === 0 ? 'nao_encontrado' : 'varios');
      }
    } catch (error: any) {
      toast.error(`Erro ao buscar no Inventário: ${error?.message || 'erro desconhecido'}`);
    } finally {
      setBuscando(false);
    }
  };

  const aoCadastrar = async (id: string) => {
    setCadastroAberto(false);
    const ativo = await equipamentoService.getById(id);
    if (ativo) escolher(ativo);
  };

  const colaborador = encontrado?.colaboradorAtualId ? nomeColaborador.get(encontrado.colaboradorAtualId) : null;
  const indisponivel =
    paraEnvio &&
    encontrado &&
    (Boolean(encontrado.colaboradorAtualId) || !SITUACOES_DISPONIVEIS.includes(situacaoDoAtivo(encontrado)));

  return (
    <div className="space-y-2">
      <div className={empilhado ? 'flex flex-col gap-3' : 'flex flex-wrap gap-2'}>
        {empilhado && <label htmlFor="busca-ativo-patrimonio" className="-mb-2 text-sm text-gray-700">Patrimônio</label>}
        <input
          id={empilhado ? 'busca-ativo-patrimonio' : undefined}
          type="text"
          className={empilhado ? 'input-gaveta' : 'input-primary flex-1'}
          placeholder={empilhado ? 'Ex.: BP001' : 'Patrimônio'}
          value={patrimonio}
          onChange={(e) => {
            onChange({ patrimonio: e.target.value.toUpperCase(), nSerie, encontrado: null });
            setEstado('inicial');
          }}
        />
        {empilhado && (
          <label htmlFor="busca-ativo-nserie" className="-mb-2 text-sm text-gray-700">
            Nº de série (se não souber o patrimônio)
          </label>
        )}
        <input
          id={empilhado ? 'busca-ativo-nserie' : undefined}
          type="text"
          className={empilhado ? 'input-gaveta' : 'input-primary flex-1'}
          placeholder={empilhado ? 'Ex.: PE0DDGF7' : 'Nº de Série'}
          value={nSerie}
          onChange={(e) => {
            onChange({ patrimonio, nSerie: e.target.value, encontrado: null });
            setEstado('inicial');
          }}
        />
        <button
          type="button"
          className={empilhado ? 'btn-secondary self-start text-sm' : 'btn-secondary text-sm'}
          onClick={buscar}
          disabled={buscando || (!patrimonio.trim() && !nSerie.trim())}
        >
          {buscando ? 'Buscando...' : 'Buscar no Inventário'}
        </button>
      </div>

      {encontrado && (
        <div className="rounded-md border border-green-200 bg-green-50 p-2 text-xs text-green-800">
          <p>
            <span className="font-semibold">No Inventário:</span>{' '}
            {[TIPO_ATIVO_LABELS[encontrado.tipo] ?? encontrado.tipo, encontrado.modelo].filter(Boolean).join(' · ')} —{' '}
            {descreverSituacao(encontrado.statusCustodia, encontrado.situacaoDetalhe ?? null)}
            {colaborador ? ` com ${colaborador}` : ''}
          </p>
          {!encontrado.modelo && !ehItemSimples(encontrado.tipo) && (
            <p className="mt-1 flex items-center gap-1 font-medium text-amber-700">
              <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />
              Sem modelo no Inventário — complete a ficha do ativo (aba Ativos); o termo usa o modelo.
            </p>
          )}
          {indisponivel && (
            <p className="mt-1 flex items-center gap-1 font-medium text-amber-700">
              <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />
              Não parece disponível pra envio — confira antes de vincular.
            </p>
          )}
        </div>
      )}

      {estado === 'nao_encontrado' && (
        <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900">
          <p>
            <span className="font-semibold">Ativo não encontrado no Inventário.</span> Deseja cadastrar agora?
          </p>
          <div className="flex gap-2">
            <button type="button" className="btn-primary text-xs" onClick={() => setCadastroAberto(true)}>
              Sim, cadastrar
            </button>
            <button type="button" className="btn-secondary text-xs" onClick={() => setEstado('recusado')}>
              Não
            </button>
          </div>
        </div>
      )}

      {estado === 'recusado' && (
        <p className="text-xs text-amber-700">
          Sem cadastro no Inventário não é possível vincular. Cadastre o ativo (aba Ativos) ou confira o patrimônio/série.
        </p>
      )}

      {estado === 'varios' && (
        <p className="text-xs text-amber-700">
          Mais de um cadastro no Inventário com esse patrimônio/série — resolva o duplicado na aba Ativos (excluir o errado)
          e busque de novo.
        </p>
      )}

      <CadastrarAtivoDrawer
        isOpen={cadastroAberto}
        inicial={{ ...cadastroInicial, patrimonio: patrimonio.trim(), nSerie: nSerie.trim() }}
        onClose={() => setCadastroAberto(false)}
        onPronto={aoCadastrar}
      />
    </div>
  );
}
